<script setup lang="ts">
type ArticleCard = {
  title: string
  slug: string
  publishedAt: string
  media: Record<string, unknown>
  callToAction: Record<string, unknown>
  seo: Record<string, unknown>
  tag: Record<string, unknown>
}

const _props = defineProps<{ article: ArticleCard; isHighlighted?: boolean }>()

const localePath = useLocalePath()
const isPodcast = computed(() => _props.article.tag?.title?.toLowerCase() === 'podcast')
</script>

<template>
  <article
    class="article-card group relative flex h-auto w-full shrink-0 overflow-clip rounded-sm bg-(--white) p-4 text-(--green-1)"
    :class="[
      {
        'aspect-square h-full text-white md:aspect-[4/3] lg:aspect-[7/5]': isHighlighted,
      },
      {
        'lg:aspect-[2/3]': !isHighlighted,
      },
      { '!text-black': isHighlighted && isPodcast },
    ]"
  >
    <nuxt-link
      :to="localePath(article.slug)"
      class="absolute inset-0 z-10 h-full w-full"
      :aria-label="article.title"
    />
    <div class="content relative z-[2] flex w-full flex-col justify-between gap-6">
      <div
        class="header flex flex-col justify-between"
        :class="isHighlighted ? '' : 'h-[300px] lg:h-[calc(50%-20px)]'"
      >
        <div class="grid gap-4">
          <span v-if="article.publishedAt" class="date text-xs font-medium uppercase">{{
            formatDateString(article.publishedAt)
          }}</span>
          <h3
            v-if="article.title"
            :class="[`${isHighlighted ? 'text-xl' : 'text-lg'}`]"
            class="font-display line-clamp-2"
          >
            {{ article.title }}
          </h3>
        </div>
        <div
          v-if="article.seo?.description && !isHighlighted && !isPodcast"
          class="body invisible grid gap-2.5 lg:visible"
        >
          <p class="max-w-cols-4 line-clamp-2 text-base">{{ article.seo?.description }}</p>
        </div>
      </div>
      <div
        v-if="article.media && isPodcast"
        class="podcast-media aspect-square h-36 w-36 overflow-clip"
      >
        <CommonMedia
          :media="article.media"
          class="h-full w-full scale-101 object-cover transition-[scale] duration-300 lg:group-hover:scale-108"
          :sizes="'xs:20vw sm:20vw md:15vw lg:10vw xl:10vw xxl:10vw'"
        />
      </div>
      <div
        v-if="article.seo?.description && (isHighlighted || isPodcast)"
        class="body grid gap-2.5"
      >
        <p class="max-w-cols-4 line-clamp-2 text-base">{{ article.seo?.description }}</p>
        <div
          v-if="isPodcast"
          class="tag flex w-fit items-center rounded-full py-1 text-(--green-1)"
        >
          <IconsPlay class="size-4" />
          <!-- NEED REAL PODCAST DURATION -->
          <p class="text-sm">00:00</p>
        </div>
      </div>
      <ElementsTag v-if="article.tag" v-bind="article.tag" />
    </div>
    <div
      v-if="article.media && !isPodcast"
      class="background-media absolute bottom-0 left-0 z-[1] flex h-full w-full"
    >
      <div
        v-if="isHighlighted"
        class="overlay absolute top-0 left-0 z-[1] h-full w-full bg-black/30"
      />
      <CommonMedia
        v-if="isHighlighted"
        :media="article.media"
        class="sc h-full w-full scale-101 overflow-clip object-cover transition-[scale] duration-300 lg:group-hover:scale-108"
        :sizes="'xs:60vw sm:60vw md:60vw lg:35vw xl:35vw xxl:35vw'"
      />
      <div v-else class="wrapper mt-auto h-1/2 w-full overflow-clip">
        <CommonMedia
          :media="article.media"
          class="h-full w-full object-cover transition-[scale] duration-300 lg:group-hover:scale-108"
          :class="article.media?.video ? 'flex items-center justify-center' : ''"
          :sizes="'xs:60vw sm:60vw md:60vw lg:15vw xl:15vw xxl:15vw'"
        />
      </div>
    </div>
  </article>
</template>

<style>
/* Fake minimum square aspect ratio */
@media (max-width: 1024px) {
  .min-aspect:before {
    padding-bottom: 100%;
    content: '';
    float: left;
  }
  .min-aspect:after {
    display: table;
    content: '';
    clear: both;
  }
}
</style>
